import { VaultKey, VaultKeys } from '@molecularlabs/nucleus-frontend';
import { AppRoute } from './shared';

export type NucleusVaultMetadata = {
  name: string;
  summary: string;
  marketId: string;
  vaultKey: VaultKey;
  vaultAddress: EthAddress;
  iconPath: string;
  logosPath: string;
  yieldGeneratingTokenSymbol: string;
  primaryComposition: TComposition & { token: string; logosPath: string; description: string };
  relatedApps: TComposition[];
  learnMoreLink: string;
  apyOverride?: number;
};

type TComposition = { subTitle: string; appId: string };
export const NUCLEUS_VAULTS: NucleusVaultMetadata[] = [
  {
    name: 'Looped nALPHA Optimizer',
    summary: 'Maximize nALPHA yield and earn PLUME incentives via this automated looping strategy',
    marketId: '0xf89bda68469012ebe5eecbdb60f3b0be88348cb4aa275af40c22f62c1326a773',
    vaultKey: VaultKeys.OPNALPHA,
    vaultAddress: '0xe3BEFC4eAA73803774F4d878417fa5e09193a75A',
    yieldGeneratingTokenSymbol: 'nALPHA',
    iconPath: '/assets/vaults/op-nalpha/nalpha.svg',
    logosPath: '/assets/vaults/op-nalpha/logos.avif',
    primaryComposition: {
      token: 'nALPHA',
      subTitle: 'Holding spot nALPHA',
      description: 'Balanced yields from high quality real-world assets',
      appId: 'nest',
      logosPath: '/assets/vaults/op-nalpha/primary-composition-logos.avif',
    },
    relatedApps: [
      { appId: 'solera', subTitle: 'nALPHA Looping' },
      { appId: 'mystic', subTitle: 'nALPHA Looping' },
    ],
    learnMoreLink: 'https://app.nest.credit/nest-rwa-vault',
    apyOverride: 0.12,
  },
  {
    name: 'Looped PLUME Staking Optimizer',
    summary: 'Maximize PLUME staking yield and earn PLUME incentives via this automated looping strategy',
    marketId: '0xbf398306282c870f89f4e604594355434f4518da6aaf5fa883d459fd69588b48',
    vaultKey: VaultKeys.OPNPLUME,
    vaultAddress: '0x8337930b8431f10f2644ddf7189c43802f76cb88',
    yieldGeneratingTokenSymbol: 'PLUME',
    iconPath: '/assets/vaults/op-plume/plume.svg',
    logosPath: '/assets/vaults/op-plume/logos.avif',
    primaryComposition: {
      token: 'PLUME',
      subTitle: 'Staking PLUME',
      description: 'Stake PLUME and delegate to validators to secure the chain and earn yield',
      appId: 'plume_staking',
      logosPath: '/assets/vaults/op-plume/primary-composition-logos.avif',
    },
    relatedApps: [
      { appId: 'solera', subTitle: 'Plume Staking Looping' },
      { appId: 'mystic', subTitle: 'Plume Staking Looping' },
    ],
    learnMoreLink: 'https://staking.plume.org/',
  },
  {
    name: 'Looped nBASIS Optimizer',
    summary: 'Maximize nBASIS yield and earn PLUME incentives via this automated looping strategy',
    marketId: '0x11c0d2e939d550da04ff69f76b699885b9d62d942dbf9465c6eb2d64fcac0939',
    vaultKey: VaultKeys.OPNBASIS,
    vaultAddress: '0x1caad1a4028f80e2f142dbae46e6af5bb78e7d98',
    yieldGeneratingTokenSymbol: 'nBASIS',
    iconPath: '/assets/vaults/op-nbasis/nbasis.svg',
    logosPath: '/assets/vaults/op-nbasis/logos.avif',
    primaryComposition: {
      token: 'nBASIS',
      subTitle: 'Holding spot nBASIS',
      description: 'Delta-neutral yield from basis trade strategies',
      appId: 'superstate',
      logosPath: '/assets/vaults/op-nbasis/primary-composition-logos.avif',
    },
    relatedApps: [
      { appId: 'solera', subTitle: 'nBasis Looping' },
      { appId: 'mystic', subTitle: 'nBasis Looping' },
    ],
    learnMoreLink: 'https://app.nest.credit/nest-basis-vault',
    apyOverride: 0.12,
  },
];

export const getVaultMetadataByAddress = (address: string | undefined) => {
  if (!address) throw new Error(`No vault found by address ${address}`);
  const vault = NUCLEUS_VAULTS.find((v) => v.vaultAddress.toLowerCase() === address.toLowerCase());
  if (!vault) throw new Error(`no vault found for address ${address}`);
  return vault;
};
export const getVaultMetadataByKey = (vaultKey: string | undefined) => {
  if (!vaultKey) throw new Error(`No vault found by key ${vaultKey}`);
  const vault = NUCLEUS_VAULTS.find((v) => v.vaultKey.toLowerCase() === vaultKey.toLowerCase());
  if (!vault) throw new Error(`no vault found for key ${vaultKey}`);
  return vault;
};
export const getVaultKeyByAddress = (address: string) => getVaultMetadataByAddress(address)?.vaultKey.toLowerCase();
export const getVaultFlowchartImageByKeyAndStep = (vaultKey: VaultKey, stepNumber: number) => {
  return `/assets/vaults/${vaultKey.toLowerCase()}/flowchart-${stepNumber}.avif`;
};

export const getVaultUrlByVaultKey = (vaultKey: VaultKey) => {
  const vaultMeta = getVaultMetadataByKey(vaultKey);
  return AppRoute.OptimizerVault.replace(':vaultAddress', vaultMeta.vaultAddress);
};
